import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from './lib/axiosClient';
import { useWorkOrderPanel } from './contexts/WorkOrderPanelContext';
import { useTenant } from './contexts/TenantContext';
import useAutoRefresh from './hooks/useAutoRefresh';
import AdminDashboard from './AdminDashboard';
import SmokeshopDashboard from './SmokeshopDashboard';
import RentalDashboard from './RentalDashboard';

function Dashboard() {
  const navigate = useNavigate();
  const { openPanel } = useWorkOrderPanel();
  const { tenant, hasFeature, businessName, isSuperAdmin } = useTenant();


  const [stats, setStats] = useState({
    todayRevenue: 0, todayBookings: 0, pendingCheckouts: 0,
    totalCustomers: 0, totalServices: 0, monthRevenue: 0
  });
  const [shop, setShop] = useState(null);
  const [features, setFeatures] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    try { return localStorage.getItem('onboarding_dismissed') === 'true'; } catch(e) { return false; }
  });
  const [skippedSteps, setSkippedSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('onboarding_skipped') || '[]'); } catch(e) { return []; }
  });
  const [expandedStep, setExpandedStep] = useState(null);

  useEffect(() => { var h = false; try { h = !!localStorage.getItem("sa_tenant_override"); } catch(e) {} if (!isSuperAdmin || h) loadData(); }, []);

  const { businessType } = useTenant();
  const bt = (businessType || '').toLowerCase();
  const isShopType = ['motorcycle','auto','automotive','mechanic','auto_repair','motorcycle_repair'].includes(bt);
  const isSpaType = ['spa','massage','salon','barbershop','barber','nails','nail_salon','hair_salon','beauty','medspa','med_spa'].includes(bt);
  const isRental = bt === 'rental';

  const loadData = async (silent) => {
    try {
      const [statsRes, servicesRes, shopRes, chartRes, bookingsRes, featuresRes, onboardingRes] = await Promise.all([
        axios.get('/api/dashboard/stats'),
        axios.get('/api/services?active_only=true'),
        axios.get('/api/dashboard/shop-stats').catch(() => null),
        axios.get('/api/dashboard/revenue-chart?days=7').catch(() => null),
        axios.get('/api/dashboard/today-bookings').catch(() => null),
        axios.get('/api/dashboard/shop-features').catch(() => null),
        axios.get('/api/dashboard/onboarding').catch(() => null),
      ]);
      if (statsRes.data?.success) setStats(statsRes.data.stats);
      if (servicesRes.data?.success) setServices(servicesRes.data.services || []);
      if (shopRes?.data?.success) setShop(shopRes.data.shop);
      if (chartRes?.data?.success) setRevenueChart(chartRes.data.data || []);
      if (bookingsRes?.data?.success) {
        setTodayBookings(bookingsRes.data.today || []);
        setUpcomingBookings(bookingsRes.data.upcoming || []);
      }
      if (featuresRes?.data?.success) setFeatures(featuresRes.data.features);
      if (onboardingRes?.data?.success) setOnboarding(onboardingRes.data.onboarding);
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      if (!silent) setLoading(false);
    }
  };

  useAutoRefresh(function() { loadData(true); }, 20000);

  // Show admin dashboard when super admin with no tenant override
  var hasTenantOverride = false;
  try { hasTenantOverride = !!localStorage.getItem('sa_tenant_override'); } catch(e) {}
  if (isSuperAdmin && !hasTenantOverride) return <AdminDashboard />;
  if (isRental) return <RentalDashboard />;
  if (bt === "smoke_shop" || bt === "smokeshop") return <SmokeshopDashboard />;

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-2xl text-slate-400">Loading...</div></div>;
  }

  var hasShop = shop && shop.openWorkOrders > 0;
  var isNewBusiness = !stats.totalCustomers && !stats.totalServices && !stats.todayRevenue && !stats.monthRevenue;

  var skipStep = function(key) {
    var updated = [...skippedSteps, key];
    setSkippedSteps(updated);
    try { localStorage.setItem('onboarding_skipped', JSON.stringify(updated)); } catch(e) {}
    setExpandedStep(null);
  };

  var unskipStep = function(key) {
    var updated = skippedSteps.filter(function(k) { return k !== key; });
    setSkippedSteps(updated);
    try { localStorage.setItem('onboarding_skipped', JSON.stringify(updated)); } catch(e) {}
  };

  var isStepDone = function(key, step) { return step.done || skippedSteps.includes(key); };

  // Find first incomplete step (accounting for skips)
  var firstIncomplete = onboarding ? Object.entries(onboarding.steps).sort(function(a, b) { return a[1].priority - b[1].priority; }).find(function(entry) { return !isStepDone(entry[0], entry[1]); }) : null;
  var activeStep = expandedStep || (firstIncomplete ? firstIncomplete[0] : null);

  // Recalculate progress with skips
  var effectiveCompleted = onboarding ? Object.entries(onboarding.steps).filter(function(entry) { return isStepDone(entry[0], entry[1]); }).length : 0;
  var effectiveTotal = onboarding ? Object.keys(onboarding.steps).length : 1;
  var effectivePercent = Math.round((effectiveCompleted / effectiveTotal) * 100);
  var effectiveAllDone = effectiveCompleted === effectiveTotal;

  var showOnboarding = onboarding && !effectiveAllDone && !onboardingDismissed;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">

      {/* ONBOARDING CHECKLIST */}
      {showOnboarding && (
        <div style={{ marginBottom: 24 }}>
          {/* Header card */}
          <div style={{
            background: 'linear-gradient(135deg, #1a1c2e, #12121f)',
            borderRadius: '20px', border: '1px solid #1e1e35', overflow: 'hidden',
          }}>
            {/* Top section with greeting & progress */}
            <div style={{ padding: '32px 32px 24px', borderBottom: '1px solid #1e1e35' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 6px' }}>
                    Welcome to IVision Marketing! 🚀
                  </h2>
                  <p style={{ color: '#888', fontSize: 14, margin: 0 }}>
                    Complete these steps to start booking customers and growing revenue.
                  </p>
                </div>
                <button onClick={() => { setOnboardingDismissed(true); try { localStorage.setItem('onboarding_dismissed', 'true'); } catch(e) {} }}
                  style={{ background: 'none', border: 'none', color: '#555', fontSize: 12, cursor: 'pointer', padding: '4px 8px', whiteSpace: 'nowrap' }}
                  title="Dismiss setup guide">
                  ✕ Dismiss
                </button>
              </div>

              {/* Progress bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, height: 8, background: '#252840', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 8,
                    background: effectivePercent === 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #ff6b2b, #ffab40)',
                    width: effectivePercent + '%',
                    transition: 'width 0.6s ease',
                  }} />
                </div>
                <span style={{ color: '#ff8f40', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {effectiveCompleted}/{effectiveTotal} done
                </span>
              </div>
            </div>

            {/* Steps list */}
            <div style={{ padding: '8px 16px' }}>
              {Object.entries(onboarding.steps)
                .sort((a, b) => a[1].priority - b[1].priority)
                .map(([key, step]) => {
                  const isActive = activeStep === key;
                  const isComplete = isStepDone(key, step);
                  const isSkipped = skippedSteps.includes(key) && !step.done;
                  return (
                    <div key={key} style={{
                      borderBottom: '1px solid #ffffff06',
                    }}>
                      {/* Step row */}
                      <button
                        onClick={() => setExpandedStep(isActive ? null : key)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 14,
                          padding: '16px 16px', background: 'none', border: 'none', cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => { if (!isComplete) e.currentTarget.style.background = '#ffffff04'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                      >
                        {/* Checkbox */}
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: step.done ? 'linear-gradient(135deg, #10b981, #34d399)' : isSkipped ? '#252840' : (isActive ? '#ff6b2b20' : '#252840'),
                          border: step.done ? 'none' : isSkipped ? '2px solid #555' : (isActive ? '2px solid #ff6b2b' : '2px solid #2a2a4a'),
                          transition: 'all 0.3s',
                        }}>
                          {step.done ? (
                            <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>
                          ) : isSkipped ? (
                            <span style={{ color: '#555', fontSize: 12 }}>—</span>
                          ) : (
                            <span style={{ color: isActive ? '#ff6b2b' : '#555', fontSize: 14 }}>{step.priority}</span>
                          )}
                        </div>

                        {/* Label */}
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{
                            fontSize: 14, fontWeight: 600,
                            color: isComplete ? '#555' : '#eee',
                            textDecoration: isComplete ? 'line-through' : 'none',
                          }}>
                            {step.icon} {step.label}
                            {isSkipped && (
                              <span style={{ color: '#555', fontSize: 11, marginLeft: 8, fontWeight: 600, fontStyle: 'italic' }}>Skipped</span>
                            )}
                            {!isSkipped && step.substeps && !isComplete && (
                              <span style={{ color: '#ff8f40', fontSize: 11, marginLeft: 8, fontWeight: 700 }}>
                                {step.substeps.filter(s => s.done).length}/{step.substeps.length} imported
                              </span>
                            )}
                            {!isSkipped && !step.substeps && step.count > 0 && !isComplete && (
                              <span style={{ color: '#ff8f40', fontSize: 11, marginLeft: 8, fontWeight: 700 }}>
                                {step.count} added
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expand arrow */}
                        {(!isComplete || isSkipped) && (
                          <span style={{ color: '#555', fontSize: 12, transition: 'transform 0.2s', transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                        )}
                      </button>

                      {/* Expanded detail — for incomplete steps */}
                      {isActive && !isComplete && (
                        <div style={{
                          padding: '0 16px 20px 58px',
                          animation: 'fadeIn 0.2s ease',
                        }}>
                          <p style={{ color: '#888', fontSize: 13, margin: '0 0 14px', lineHeight: 1.6 }}>
                            {step.desc}
                          </p>

                          {/* Substep indicators for import step */}
                          {step.substeps && (
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                              {step.substeps.map((sub, si) => (
                                <div key={si} style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  padding: '5px 12px', borderRadius: 8,
                                  background: sub.done ? '#10b98115' : '#ffffff06',
                                  border: sub.done ? '1px solid #10b98130' : '1px solid #ffffff0a',
                                }}>
                                  <span style={{ fontSize: 11, color: sub.done ? '#34d399' : '#555' }}>{sub.done ? '✓' : '○'}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: sub.done ? '#34d399' : '#888' }}>{sub.label}</span>
                                  {sub.count > 0 && <span style={{ fontSize: 10, color: sub.done ? '#10b981' : '#ff8f40', fontWeight: 700 }}>{sub.count}</span>}
                                </div>
                              ))}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => navigate(step.path)}
                              style={{
                                background: 'linear-gradient(135deg, #ff6b2b, #ffab40)',
                                border: 'none', borderRadius: 10, color: '#fff',
                                fontSize: 13, fontWeight: 700, padding: '10px 24px',
                                cursor: 'pointer', transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                            >
                              {step.label} →
                            </button>

                            {/* Manual add links for import step */}
                            {step.substeps && (
                              <span style={{ fontSize: 12, color: '#555' }}>
                                or add manually:
                                {' '}<span onClick={() => navigate('/app/services')} style={{ color: '#888', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Services</span>
                                {' · '}<span onClick={() => navigate('/app/customers')} style={{ color: '#888', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Customers</span>
                                {' · '}<span onClick={() => navigate('/app/staff')} style={{ color: '#888', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>Staff</span>
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => skipStep(key)}
                            style={{ background: 'none', border: 'none', color: '#555', fontSize: 12, cursor: 'pointer', padding: '8px 0 0', display: 'block' }}
                          >
                            Skip this step →
                          </button>
                        </div>
                      )}

                      {/* Expanded detail — for SKIPPED steps (undo option) */}
                      {isActive && isSkipped && (
                        <div style={{
                          padding: '0 16px 20px 58px',
                          animation: 'fadeIn 0.2s ease',
                        }}>
                          <p style={{ color: '#888', fontSize: 13, margin: '0 0 14px', lineHeight: 1.6 }}>
                            You skipped this step. You can undo to continue where you left off.
                          </p>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => { unskipStep(key); navigate(step.path); }}
                              style={{
                                background: 'linear-gradient(135deg, #ff6b2b, #ffab40)',
                                border: 'none', borderRadius: 10, color: '#fff',
                                fontSize: 13, fontWeight: 700, padding: '10px 24px',
                                cursor: 'pointer', transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                            >
                              {step.label} →
                            </button>
                            <button
                              onClick={() => unskipStep(key)}
                              style={{ background: 'none', border: '1px solid #333', borderRadius: 10, color: '#aaa', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '8px 18px', transition: 'all 0.2s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b2b'; e.currentTarget.style.color = '#ff6b2b'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#aaa'; }}
                            >
                              Undo Skip
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Booking link teaser */}
            {tenant?.subdomain && (
              <div style={{
                padding: '16px 32px', background: '#ff6b2b06', borderTop: '1px solid #ff6b2b12',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <span style={{ color: '#888', fontSize: 12 }}>Your booking page: </span>
                  <span style={{ color: '#ff8f40', fontSize: 12, fontWeight: 600 }}>ivisionmarketing.com/book/{tenant.subdomain}</span>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText('https://ivisionmarketing.com/book/' + tenant.subdomain); }}
                  style={{ background: '#ff6b2b15', border: '1px solid #ff6b2b20', borderRadius: 8, color: '#ff8f40', fontSize: 11, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}
                >
                  Copy Link
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Minimal congrats banner when all done (shows once) */}
      {onboarding && effectiveAllDone && !onboardingDismissed && (
        <div style={{
          background: 'linear-gradient(135deg, #10b98115, #34d39908)', borderRadius: 16, padding: '20px 28px',
          border: '1px solid #10b98130', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <span style={{ fontSize: 20, marginRight: 10 }}>🎉</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Setup complete!</span>
            <span style={{ color: '#888', fontSize: 14, marginLeft: 8 }}>You're ready to start taking bookings and growing your business.</span>
          </div>
          <button onClick={() => { setOnboardingDismissed(true); try { localStorage.setItem('onboarding_dismissed', 'true'); } catch(e) {} }}
            style={{ background: '#10b98120', border: '1px solid #10b98130', borderRadius: 8, color: '#34d399', fontSize: 12, fontWeight: 600, padding: '8px 16px', cursor: 'pointer' }}>
            Got it ✓
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Today's Revenue" value={'$' + Number(stats.todayRevenue || 0).toFixed(2)} color="text-emerald-500" icon="💰" to="/app/checkout" />
        <StatCard label="This Month" value={'$' + Number(stats.monthRevenue || 0).toFixed(2)} color="text-emerald-400" icon="📊" to="/app/checkout" />
        <StatCard label="Today's Bookings" value={stats.todayBookings || 0} color="text-primary" icon="📅" to="/app/bookings" />
        <StatCard label="Total Customers" value={stats.totalCustomers || 0} color="text-blue-400" icon="👥" to="/app/customers" />
      </div>

      {/* TODAY'S APPOINTMENTS */}
      <div className="bg-surface p-6 rounded-lg border border-slate-700 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">📅 Today's Appointments</h2>
          <Link to="/app/bookings" className="text-primary text-sm font-semibold hover:underline">View Calendar →</Link>
        </div>
        {todayBookings.length > 0 ? (
          <div className="space-y-3">
            {todayBookings.map(b => {
              const startParts = b.start_time?.split(':').map(Number) || [0,0];
              const hour = startParts[0] > 12 ? startParts[0] - 12 : startParts[0] === 0 ? 12 : startParts[0];
              const ampm = startParts[0] >= 12 ? 'PM' : 'AM';
              const timeStr = hour + ':' + String(startParts[1]).padStart(2, '0') + ' ' + ampm;
              const statusColors = {
                confirmed: 'bg-emerald-900/50 text-emerald-300',
                pending: 'bg-amber-900/50 text-amber-300',
                completed: 'bg-blue-900/50 text-blue-300',
                cancelled: 'bg-red-900/50 text-red-300',
              };
              return (
                <div key={b.id} className="flex items-center justify-between bg-background p-4 rounded-lg border border-slate-700 hover:border-primary transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <div className="text-primary font-bold text-lg">{timeStr}</div>
                      <div className="text-slate-500 text-xs">{b.duration || b.services?.duration}min</div>
                    </div>
                    <div>
                      <div className="text-white font-semibold">{b.contacts?.first_name} {b.contacts?.last_name}</div>
                      <div className="text-slate-400 text-sm">{b.services?.name}</div>
                      {b.staff && <div className="text-slate-500 text-xs mt-1">w/ {b.staff.first_name} {b.staff.last_name}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-emerald-400 font-bold">${parseFloat(b.final_price || b.services?.price || 0).toFixed(0)}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[b.status] || 'bg-slate-700 text-slate-300'}`}>{b.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">📭</div>
            <p className="text-slate-500 text-sm">No appointments scheduled for today</p>
            <Link to="/app/bookings" className="text-primary text-sm font-semibold hover:underline mt-2 inline-block">+ Book an appointment</Link>
          </div>
        )}
      </div>

      {/* UPCOMING BOOKINGS */}
      {upcomingBookings.length > 0 && (
        <div className="bg-surface p-6 rounded-lg border border-slate-700 mb-6">
          <h2 className="text-lg font-bold text-white mb-4">📋 Upcoming This Week</h2>
          <div className="space-y-2">
            {upcomingBookings.map(b => {
              const startParts = b.start_time?.split(':').map(Number) || [0,0];
              const hour = startParts[0] > 12 ? startParts[0] - 12 : startParts[0] === 0 ? 12 : startParts[0];
              const ampm = startParts[0] >= 12 ? 'PM' : 'AM';
              const timeStr = hour + ':' + String(startParts[1]).padStart(2, '0') + ' ' + ampm;
              const dateStr = new Date(b.booking_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <div key={b.id} className="flex items-center justify-between bg-background p-3 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-400 min-w-[90px]">{dateStr}</div>
                    <div className="text-primary font-semibold text-sm min-w-[70px]">{timeStr}</div>
                    <div className="text-white text-sm">{b.contacts?.first_name} {b.contacts?.last_name}</div>
                    <div className="text-slate-500 text-sm hidden md:block">— {b.services?.name}</div>
                  </div>
                  <span className="text-emerald-400 font-bold text-sm">${parseFloat(b.final_price || b.services?.price || 0).toFixed(0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SHOP SECTION */}
      {/* Spa: Today's Appointments */}
      {isSpaType && (
        <div style={{ background: '#12121f', borderRadius: 16, padding: '24px', marginBottom: 24, border: '1px solid #1e1e3a' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>📅 Today's Appointments</h2>
            <button onClick={() => navigate('/app/bookings')} style={{ padding: '6px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>View All</button>
          </div>
          {todayBookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#555' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🌿</div>
              <div style={{ fontSize: 14 }}>No appointments scheduled for today</div>
              <button onClick={() => navigate('/app/bookings')} style={{ marginTop: 12, padding: '8px 20px', background: 'transparent', border: '1px solid #2a2a4a', borderRadius: 8, color: '#a78bfa', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>+ Book an appointment</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todayBookings.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#1a1c2e', borderRadius: 10, border: '1px solid #1e1e3a' }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, color: '#fff', flexShrink: 0 }}>
                    {(b.contact_name || b.customer_name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>{b.contact_name || b.customer_name || 'Guest'}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{b.service_name || 'Appointment'} {b.staff_name ? '· ' + b.staff_name : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>{b.start_time ? new Date(b.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    <div style={{ fontSize: 11, color: b.status === 'confirmed' ? '#22c55e' : '#f59e0b', marginTop: 2, fontWeight: 600 }}>{b.status || 'pending'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(shop || isShopType) && !isSpaType && (
        <>
          {/* WORK ORDER STATS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Open Work Orders"
              to="/app/work-orders"
              value={shop?.openWorkOrders || 0}
              color="text-amber-400"
              icon="🔧"
              sub={'$' + Number(shop?.woValue || 0).toFixed(2) + ' total value'}
            />
            <StatCard
              label="Ready for Pickup"
              to="/app/work-orders"
              value={shop?.readyForPickup?.length || 0}
              color="text-emerald-400"
              icon="🏁"
              pulse={shop?.readyForPickup?.length > 0}
            />
            <StatCard
              label="Outstanding Invoices"
              to="/app/invoices"
              value={shop?.outstandingInvoices || 0}
              color="text-red-400"
              icon="📄"
              sub={'$' + Number(shop?.outstandingAmount || 0).toFixed(2) + ' owed'}
            />
            <StatCard
              label="Pending Estimates"
              to="/app/estimates"
              value={shop?.pendingEstimates?.length || 0}
              color="text-violet-400"
              icon="📝"
            />
          </div>

          {/* WORK ORDER PIPELINE */}
          <div className="bg-surface p-6 rounded-lg border border-slate-700 mb-6">
            <h2 className="text-lg font-bold text-white mb-4">🔧 Work Order Pipeline <span className="text-sm text-slate-500 font-normal">— click to expand</span></h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { key: 'received', label: 'Received', icon: '📥', bg: 'bg-blue-900/30 border-blue-700' },
                { key: 'diagnosed', label: 'Diagnosed', icon: '🔍', bg: 'bg-amber-900/30 border-amber-700' },
                { key: 'approved', label: 'Approved', icon: '👍', bg: 'bg-violet-900/30 border-violet-700' },
                { key: 'in_progress', label: 'In Progress', icon: '🔧', bg: 'bg-orange-900/30 border-orange-700' },
                { key: 'quality_check', label: 'QC', icon: '✅', bg: 'bg-teal-900/30 border-teal-700' },
                { key: 'ready', label: 'Ready', icon: '🏁', bg: 'bg-emerald-900/30 border-emerald-700' },
              ].map(s => (
                <div key={s.key}
                  onClick={() => setPipelineStatus(pipelineStatus === s.key ? null : s.key)}
                  className={`${s.bg} border rounded-lg p-3 text-center cursor-pointer hover:scale-105 transition-all ${pipelineStatus === s.key ? 'ring-2 ring-white shadow-lg scale-105' : ''}`}>
                  <div className="text-xl mb-1">{s.icon}</div>
                  <div className="text-2xl font-bold text-white">{(shop?.statusCounts || {})[s.key] || 0}</div>
                  <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Expanded Work Orders for selected status */}
            {pipelineStatus && (
              <div className="mt-4 border-t border-slate-700 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-white font-semibold">
                    {pipelineStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} ({(shop?.workOrders || []).filter(wo => wo.status === pipelineStatus).length})
                  </h3>
                  <button onClick={() => setPipelineStatus(null)} className="text-slate-400 hover:text-white text-sm">✕ Close</button>
                </div>
                <div className="space-y-2">
                  {(shop?.workOrders || []).filter(wo => wo.status === pipelineStatus).map(wo => (
                    <div key={wo.id} onClick={() => openPanel(wo.id)} style={{cursor:'pointer'}}
                      className="block bg-background p-3 rounded-lg border border-slate-700 hover:border-primary transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-primary">{wo.order_number}</span>
                            {wo.priority === 'urgent' && <span className="text-xs bg-red-900/50 text-red-300 px-2 py-0.5 rounded-full">URGENT</span>}
                            {wo.priority === 'high' && <span className="text-xs bg-orange-900/50 text-orange-300 px-2 py-0.5 rounded-full">HIGH</span>}
                          </div>
                          <div className="text-white font-semibold text-sm mt-1">{wo.title}</div>
                          <div className="text-slate-400 text-xs mt-1">
                            {wo.contacts?.first_name} {wo.contacts?.last_name}
                            {wo.vehicles && <span> • {wo.vehicles.year} {wo.vehicles.make} {wo.vehicles.model}</span>}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(wo.created_at).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(shop?.workOrders || []).filter(wo => wo.status === pipelineStatus).length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-4">No work orders in this status</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* READY FOR PICKUP + OUTSTANDING INVOICES */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Ready for Pickup */}
            <div className="bg-surface p-6 rounded-lg border border-emerald-700/50">
              <h2 className="text-lg font-bold text-emerald-400 mb-4">🏁 Ready for Pickup</h2>
              {shop.readyForPickup?.length > 0 ? (
                <div className="space-y-3">
                  {shop.readyForPickup.map(wo => (
                    <div key={wo.id} onClick={() => openPanel(wo.id)} style={{cursor:'pointer'}}
                      className="block bg-background p-3 rounded-lg border border-slate-700 hover:border-emerald-600 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-xs font-mono text-emerald-500">{wo.order_number}</span>
                          <div className="text-white font-semibold text-sm">{wo.title}</div>
                          <div className="text-slate-400 text-xs mt-1">
                            {wo.contacts?.first_name} {wo.contacts?.last_name}
                            {wo.vehicles && <span> • {wo.vehicles.year} {wo.vehicles.make} {wo.vehicles.model}</span>}
                          </div>
                        </div>
                        <span className="text-emerald-400 text-lg">📞</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-500 text-sm text-center py-4">No vehicles ready for pickup</div>
              )}
            </div>

            {/* Outstanding Invoices */}
            <div className="bg-surface p-6 rounded-lg border border-red-700/50">
              <h2 className="text-lg font-bold text-red-400 mb-4">📄 Outstanding Invoices</h2>
              {shop.invoices?.length > 0 ? (
                <div className="space-y-3">
                  {shop.invoices.map(inv => {
                    const owed = parseFloat(inv.total || 0) - parseFloat(inv.amount_paid || 0);
                    return (
                      <Link to="/app/invoices" key={inv.id}
                        className="block bg-background p-3 rounded-lg border border-slate-700 hover:border-red-600 transition-colors">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-xs font-mono text-red-400">{inv.invoice_number}</span>
                            <div className="text-white text-sm">{inv.contacts?.first_name} {inv.contacts?.last_name}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-red-400 font-bold">${owed.toFixed(2)}</div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              inv.status === 'overdue' ? 'bg-red-900/50 text-red-300' :
                              inv.status === 'partial' ? 'bg-amber-900/50 text-amber-300' :
                              'bg-blue-900/50 text-blue-300'
                            }`}>{inv.status}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-slate-500 text-sm text-center py-4">No outstanding invoices</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* SHOP FEATURES — Inspections, Time Tracking, Low Stock, Service Due */}
      {features && !isSpaType && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-white mb-4">🏍️ Shop Status</h2>

          {/* Feature stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-surface p-4 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-400 text-xs">Active Inspections</span>
                <span className="text-lg">🔍</span>
              </div>
              <div className="text-2xl font-bold text-violet-400">{features.inspections.active}</div>
              <div className="text-xs text-slate-500">{features.inspections.completedToday} completed today</div>
            </div>
            <div className="bg-surface p-4 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-400 text-xs">Mechanics Clocked In</span>
                <span className="text-lg">⏱️</span>
              </div>
              <div className="text-2xl font-bold text-blue-400">{features.timeTracking.activeClocks}</div>
              <div className="text-xs text-slate-500">{features.timeTracking.hoursToday}h logged today</div>
            </div>
            <div className="bg-surface p-4 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-400 text-xs">Low Stock Parts</span>
                <span className="text-lg">⚠️</span>
              </div>
              <div className={`text-2xl font-bold ${features.lowStockCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{features.lowStockCount}</div>
              <div className="text-xs text-slate-500">{features.lowStockCount > 0 ? 'Need reorder' : 'All stocked'}</div>
            </div>
            <div className="bg-surface p-4 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-400 text-xs">Service Due</span>
                <span className="text-lg">🔔</span>
              </div>
              <div className={`text-2xl font-bold ${features.serviceDueCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{features.serviceDueCount}</div>
              <div className="text-xs text-slate-500">{features.serviceDueCount > 0 ? 'Bikes need service' : 'All caught up'}</div>
            </div>
            <div className="bg-surface p-4 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-400 text-xs">Photos Today</span>
                <span className="text-lg">📷</span>
              </div>
              <div className="text-2xl font-bold text-sky-400">{features.photosToday}</div>
              <div className="text-xs text-slate-500">Check-in photos</div>
            </div>
          </div>

          {/* Detail rows */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Active Time Clocks */}
            {features.timeTracking.activeList.length > 0 && (
              <div className="bg-surface p-4 rounded-lg border border-blue-700/50">
                <h3 className="text-sm font-bold text-blue-400 mb-3">⏱️ Active Clocks</h3>
                <div className="space-y-2">
                  {features.timeTracking.activeList.map(entry => (
                    <div key={entry.id} onClick={() => openPanel(entry.work_order_id)} style={{cursor:'pointer'}}
                      className="flex items-center justify-between bg-background p-2 rounded-lg border border-slate-700 hover:border-blue-500 transition-colors">
                      <div>
                        <div className="text-white text-sm font-semibold">{entry.staff?.first_name} {entry.staff?.last_name}</div>
                        <div className="text-slate-500 text-xs">{entry.work_orders?.order_number} — {entry.work_orders?.title}</div>
                      </div>
                      <div className="text-blue-400 text-xs font-mono">
                        {new Date(entry.start_time).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit'})}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Low Stock */}
            {features.lowStock.length > 0 && (
              <div className="bg-surface p-4 rounded-lg border border-red-700/50">
                <h3 className="text-sm font-bold text-red-400 mb-3">⚠️ Low Stock Parts</h3>
                <div className="space-y-2">
                  {features.lowStock.map(p => (
                    <div key={p.id} className="flex items-center justify-between bg-background p-2 rounded-lg border border-slate-700">
                      <div>
                        <div className="text-white text-sm font-semibold">{p.name}</div>
                        {p.sku && <div className="text-slate-500 text-xs">SKU: {p.sku}</div>}
                      </div>
                      <div className={`text-sm font-bold ${p.current_stock <= 0 ? 'text-red-400' : 'text-amber-400'}`}>
                        {p.current_stock} left
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/app/inventory" className="text-red-400 text-xs font-semibold hover:underline mt-2 inline-block">View Inventory →</Link>
              </div>
            )}

            {/* Service Due */}
            {features.serviceDue.length > 0 && (
              <div className="bg-surface p-4 rounded-lg border border-amber-700/50">
                <h3 className="text-sm font-bold text-amber-400 mb-3">🔔 Service Due Soon</h3>
                <div className="space-y-2">
                  {features.serviceDue.map(v => (
                    <Link to="/app/vehicles" key={v.id}
                      className="flex items-center justify-between bg-background p-2 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors">
                      <div>
                        <div className="text-white text-sm font-semibold">{v.year} {v.make} {v.model}</div>
                        <div className="text-slate-500 text-xs">{v.owner} · {v.mileage?.toLocaleString()} mi</div>
                      </div>
                      <div className="text-amber-400 text-xs font-bold">
                        {v.miles_until_service} mi left
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/app/vehicles" className="text-amber-400 text-xs font-semibold hover:underline mt-2 inline-block">View All Vehicles →</Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* REVENUE CHART */}
      {revenueChart.length > 0 && (
        <div className="bg-surface p-6 rounded-lg border border-slate-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-white">📈 Revenue (Last 7 Days)</h2>
            <span className="text-emerald-400 text-sm font-bold">${revenueChart.reduce((s, d) => s + (d.revenue || 0), 0).toFixed(0)} total</span>
          </div>
          <div className="flex items-end gap-3" style={{height: '220px'}}>
            {revenueChart.map((day, i) => {
              const max = Math.max(...revenueChart.map(d => d.revenue), 1);
              const pct = (day.revenue / max) * 100;
              const isToday = new Date(day.date + 'T12:00:00').toDateString() === new Date().toDateString();
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <div className="text-xs font-bold" style={{color: day.revenue > 0 ? '#00b894' : '#555'}}>{day.revenue > 0 ? '$' + day.revenue.toFixed(0) : '$0'}</div>
                  <div className="w-full rounded-t-lg transition-all" style={{
                    height: day.revenue > 0 ? Math.max(pct, 8) + '%' : '4px',
                    background: day.revenue > 0 ? 'linear-gradient(to top, #00b894, #00cec9)' : '#1e1e35',
                    opacity: day.revenue > 0 ? 0.8 : 0.5,
                    minHeight: '4px',
                  }} />
                  <div className="text-xs font-semibold" style={{color: isToday ? '#ff6b2b' : '#666'}}>{new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', {weekday: 'short'})}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* QUICK ACTIONS */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-white mb-4">⚡ Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {hasFeature('bookings') && <Link to="/app/bookings" className="bg-primary hover:bg-purple-600 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">📅 New Booking</Link>}
          <Link to="/app/checkout" className="bg-emerald-600 hover:bg-emerald-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">💵 Quick Checkout</Link>
          <Link to="/app/customers" className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">👥 Customers</Link>
          {hasFeature('work_orders') && <Link to="/app/work-orders" className="bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">🔧 Work Orders</Link>}
          {hasFeature('estimates') && <Link to="/app/estimates" className="bg-violet-600 hover:bg-violet-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">📝 Estimates</Link>}
          {hasFeature('invoices') && <Link to="/app/invoices" className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">📄 Invoices</Link>}
          {hasFeature('vehicles') && <Link to="/app/vehicles" className="bg-slate-600 hover:bg-slate-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">🏍️ Vehicles</Link>}
          {hasFeature('inventory') && <Link to="/app/inventory" className="bg-teal-600 hover:bg-teal-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">📦 Inventory</Link>}
          {hasFeature('reminders') && <Link to="/app/reminders" className="bg-cyan-600 hover:bg-cyan-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">🔔 Reminders</Link>}
          <a href={'/tv/' + (tenant?.subdomain || 'shop')} target="_blank" rel="noopener noreferrer" className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-lg font-semibold text-center transition-colors text-sm">📺 Lobby TV</a>
        </div>
      </div>

      {/* SERVICES */}
      {services.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-white mb-4">🛠️ Services ({services.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.slice(0, 6).map(service => (
              <div key={service.id} className="bg-surface p-4 rounded-lg border border-slate-700 hover:border-primary transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-white">{service.name}</h3>
                  <span className="text-emerald-500 font-bold">${service.price}</span>
                </div>
                <p className="text-slate-400 text-sm mb-2">{service.description}</p>
                <div className="text-slate-500 text-xs">{service.duration} minutes</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon, sub, pulse, to }) {
  return (
    <div className={`bg-surface p-5 rounded-lg border border-slate-700 ${pulse ? 'border-emerald-500/50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-slate-400 text-sm">{label}</div>
        <span className="text-xl">{icon}</span>
      </div>
      <div className={`text-3xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default Dashboard;
// cache-bust 1772894659
